import { useState, useCallback } from 'react';

/**
 * Custom React hook for accessing native device camera using Cordova Camera plugin.
 * 
 * @returns {Object} Camera utilities
 * @property {Function} takePhoto - Function to take a photo
 * @property {Function} selectFromGallery - Function to select photo from gallery
 * @property {string|null} photo - Base64 encoded photo or URI
 * @property {boolean} loading - Loading state
 * @property {string|null} error - Error message if any
 * @property {Function} clearPhoto - Function to clear the current photo
 * @property {Function} clearError - Function to clear the error
 */
const useCamera = () => {
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Take a photo using the device camera
   * @param {Object} options - Camera options
   * @param {number} options.quality - Photo quality (0-100), default 80
   * @param {number} options.destinationType - 0=DATA_URL (base64), 1=FILE_URI, default 0
   * @param {number} options.encodingType - 0=JPEG, 1=PNG, default 0
   * @param {number} options.targetWidth - Target width in pixels
   * @param {number} options.targetHeight - Target height in pixels
   * @param {boolean} options.saveToPhotoAlbum - Save to photo album, default false
   * @param {boolean} options.correctOrientation - Auto-correct orientation, default true
   */
  const takePhoto = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);

    // Check if Cordova camera plugin is available
    if (!window.navigator.camera) {
      const errorMsg = 'Camera plugin not available. Running in browser mode.';
      console.warn(errorMsg);
      setError(errorMsg);
      setLoading(false);
      return;
    }

    const defaultOptions = {
      quality: 80,
      destinationType: window.Camera.DestinationType.DATA_URL,
      sourceType: window.Camera.PictureSourceType.CAMERA,
      encodingType: window.Camera.EncodingType.JPEG,
      mediaType: window.Camera.MediaType.PICTURE,
      correctOrientation: true,
      saveToPhotoAlbum: false,
      ...options
    };

    return new Promise((resolve, reject) => {
      window.navigator.camera.getPicture(
        (imageData) => {
          const photoData = defaultOptions.destinationType === window.Camera.DestinationType.DATA_URL
            ? `data:image/jpeg;base64,${imageData}`
            : imageData;
          
          setPhoto(photoData);
          setLoading(false);
          resolve(photoData);
        },
        (err) => {
          const errorMsg = `Camera error: ${err}`;
          console.error(errorMsg);
          setError(errorMsg);
          setLoading(false);
          reject(err);
        },
        defaultOptions
      );
    });
  }, []);

  /**
   * Select a photo from the device gallery
   * @param {Object} options - Camera options (same as takePhoto)
   */
  const selectFromGallery = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);

    if (!window.navigator.camera) {
      const errorMsg = 'Camera plugin not available. Running in browser mode.';
      console.warn(errorMsg);
      setError(errorMsg);
      setLoading(false);
      return;
    }

    const defaultOptions = {
      quality: 80,
      destinationType: window.Camera.DestinationType.DATA_URL,
      sourceType: window.Camera.PictureSourceType.PHOTOLIBRARY,
      encodingType: window.Camera.EncodingType.JPEG,
      mediaType: window.Camera.MediaType.PICTURE,
      correctOrientation: true,
      ...options
    };

    return new Promise((resolve, reject) => {
      window.navigator.camera.getPicture(
        (imageData) => {
          const photoData = defaultOptions.destinationType === window.Camera.DestinationType.DATA_URL
            ? `data:image/jpeg;base64,${imageData}`
            : imageData;
          
          setPhoto(photoData);
          setLoading(false);
          resolve(photoData);
        },
        (err) => {
          const errorMsg = `Gallery error: ${err}`;
          console.error(errorMsg);
          setError(errorMsg);
          setLoading(false);
          reject(err);
        },
        defaultOptions
      );
    });
  }, []);

  const clearPhoto = useCallback(() => {
    setPhoto(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    takePhoto,
    selectFromGallery,
    photo,
    loading,
    error,
    clearPhoto,
    clearError,
  };
};

export default useCamera;
